/**
 * sign-email — email entry step of the passwordless OTP sign-in flow.
 *
 * Dispatches:
 *   sign-email-submit      — user submitted their email address
 *   sign-email-no-account  — user chose "No account? Request access", carrying
 *                            the address typed so far; offered only with
 *                            `.noAccount`, since sendOTP never says whether an
 *                            address is known
 *
 * Slots:
 *   brand — the identity mark above the field. The step is shared by every
 *           surface that signs a person in, and those surfaces do not share
 *           one identity: the storefront/admin card is the workspace's
 *           `<brand-logo>`, while the scanner is a door phone whose own
 *           identity is check-in, not the promoter's wordmark. The step owns
 *           the field and the intent; whose mark sits above it is the
 *           consumer's to say, so there is no fallback here.
 *
 * Data down via properties (.loading, .error, .addressError, .email,
 * .noAccount). Intent up via events.
 */
import { gravity } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";

export class SignEmailSubmitEvent extends CustomEvent<{ email: string }> {
	static readonly eventName = "sign-email-submit" as const;
	constructor(email: string) {
		super(SignEmailSubmitEvent.eventName, {
			detail: { email },
			bubbles: true,
			composed: true,
		});
	}
}

export class SignEmailNoAccountEvent extends CustomEvent<{ email: string }> {
	static readonly eventName = "sign-email-no-account" as const;
	constructor(email: string) {
		super(SignEmailNoAccountEvent.eventName, {
			detail: { email },
			bubbles: true,
			composed: true,
		});
	}
}

@schmancyElement("sign-email")
export class SignEmailStep extends LocalizeMixin(SchmancyElement) {
	@property({ type: Boolean }) loading = false;
	@property({ type: Boolean }) passkeySupported = false;
	@property({ type: Boolean }) passkeyLoading = false;
	@property({ type: String }) passkeyError = "";
	/** A refused send — answered at the Send button. */
	@property({ type: String }) error = "";
	/** A verdict on the address itself — answered at the field. */
	@property({ type: String }) addressError = "";

	/** The address in the field — seeded by the host when the person comes
	 *  back to edit it, then the field's own. */
	@property({ attribute: false }) email = "";
	/** Offer the person-chosen "No account? Request access" link. */
	@property({ type: Boolean }) noAccount = false;
	/** An embedded sign-in can omit the brand and its reserved region. */
	@property({ type: Boolean, attribute: "hide-brand" }) hideBrand = false;

	private submit() {
		const trimmed = this.email.trim();
		if (!trimmed.includes("@") || this.loading) return;
		this.dispatchEvent(new SignEmailSubmitEvent(trimmed));
	}

	override render() {
		const canSubmit = this.email.trim().includes("@");

		return html`
      <div class="flex flex-col gap-6 px-4 py-6 sm:px-6">
        ${when(
					!this.hideBrand,
					() => html`
          <div ${gravity({ delay: 0, distance: 20 })} class="flex flex-col gap-1">
            <slot name="brand"></slot>
          </div>
        `,
				)}

        <div
          ${gravity({ delay: 60, distance: 16 })}
          class="flex flex-col gap-3"
        >
          <schmancy-input
            label=${this.t("auth.signEmail.emailLabel")}
            type="email"
            inputmode="email"
            autocomplete=${this.passkeySupported ? "email webauthn" : "email"}
            placeholder=${this.t("auth.signEmail.emailPlaceholder")}
            .value=${this.email}
            ?disabled=${this.loading || this.passkeyLoading}
            ?error=${!!this.addressError}
            hint=""
            @input=${(e: CustomEvent<{ value: string }>) => {
							if (!this.loading) this.email = e.detail?.value ?? "";
						}}
            @enter=${() => this.submit()}
          ></schmancy-input>
          ${when(
						this.addressError,
						() => html`<schmancy-typography
              type="body"
              token="md"
              class="text-error-default"
              role="alert"
              >${this.addressError}</schmancy-typography
            >`,
					)}
          <schmancy-button
            variant="filled"
            class="self-start"
            size="md"
            ?disabled=${!canSubmit || this.passkeyLoading}
            .busy=${this.loading}
            @click=${() => this.submit()}
          >
            ${this.t("auth.signEmail.sendCode")}
          </schmancy-button>
          <!-- A refused send is the button's answer, not the address's
               fault; below it, nothing above moves when it appears. -->
          ${when(
						this.error,
						() => html`<schmancy-typography
              type="body"
              token="md"
              class="text-error-default"
              role="alert"
              >${this.error}</schmancy-typography
            >`,
					)}
          ${when(
						this.passkeySupported,
						() => html`<div class="flex flex-col gap-3">
              <schmancy-button variant="outlined" class="self-start" size="md" ?disabled=${this.loading} .busy=${this.passkeyLoading} @click=${() => this.dispatchEvent(new CustomEvent("sign-passkey", { bubbles: true, composed: true }))}>
                <schmancy-icon slot="prefix">fingerprint</schmancy-icon>${this.t("auth.signEmail.passkey")}
              </schmancy-button>
              ${when(this.passkeyError, () => html`<schmancy-typography type="body" token="md" class="text-error-default" role="alert">${this.passkeyError}</schmancy-typography>`)}
            </div>`,
					)}
        </div>
        ${when(
					this.noAccount,
					() => html`<schmancy-button
            variant="text"
            class="self-start"
            size="md"
            ?disabled=${this.loading || this.passkeyLoading}
            @click=${() =>
							this.dispatchEvent(
								new SignEmailNoAccountEvent(this.email.trim()),
							)}
          >${this.t("auth.signEmail.noAccount")}</schmancy-button>`,
				)}
      </div>
    `;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"sign-email": SignEmailStep;
	}
}
